import React, { FC, useEffect } from 'react';

import isEqual from 'lodash/isEqual';

import { useModalContext } from '~/contexts/modal/ModalContext';
import { getPWADeviceName } from '~/features/pwa/helpers';
import AddToHomeModalV1 from '~/features/pwa/versions/V1/AddToHomeModal';
import ExtensionPromoModal from '~/modules/extension/components/PromoModal';
import projectConfig, { PROJECT } from '~/projects/config';
import { GB_FEATURE_KEYS } from '~/services/abTests/features';
import { growthBook } from '~/services/abTests/growthBook';
import analytics from '~/services/analytics';
import DateService from '~/services/Date';
import extension from '~/services/Extension';
import sessionCounter from '~/services/sessionCounter';
import { useActions } from '~/store/hooks/useActions';
import { useAppSelector } from '~/store/hooks/useAppSelector';
import { PWAModalExtraFieldsType } from '~/types/profile';

type Props = {
  children?: React.ReactNode;
};

export const SESSION_TO_TRIGGER_COUNT = 'session_to_trigger_count';

const PromoModalOrchestrator: FC<Props> = ({ children }) => {
  const currentSessionCount = sessionCounter.getCurrentCount();
  const profile = useAppSelector(
    (state) => state.profile,
    (state1, state2) => !isEqual(state1.extra_fields, state2.extra_fields),
  );
  const { updateUserExtraFields } = useActions();
  const { handleOpenModal } = useModalContext();

  const handleShowPWAModal = (
    extra: Partial<PWAModalExtraFieldsType>,
    analytic_reason_property_value: string,
  ): void => {
    const deviceName = getPWADeviceName();

    if (deviceName === null) {
      return;
    }

    const eventProperties = {
      reason: analytic_reason_property_value,
      version: 'v1',
    };

    handleOpenModal({
      onClose: () =>
        analytics.trackEvent('retention - close pwa modal', eventProperties),
      maxWidth: '460px',
      hideCloseButton: true,
      disableBackdropClick: true,
      paddingSize: '24px',
      component: ({ onClose }) => (
        <AddToHomeModalV1
          device={deviceName}
          onSubmit={(): void => {
            analytics.trackEvent(
              'retention - click pwa modal',
              eventProperties,
            );
            onClose();
          }}
        />
      ),
    });

    analytics.trackEvent('retention - pwa modal', eventProperties);

    updateUserExtraFields({
      pwa_modal: {
        ...profile.extra_fields.pwa_modal,
        last_showed_at: new Date().toISOString(),
        ...extra,
      },
    });
  };

  const handleShowExtensionModal = (): void => {
    handleOpenModal({
      disableBackdropClick: true,
      closeButtonColor: 'var(--color-custom-neutral_30)',
      maxWidth: '1000px',
      paddingSize: '0px',
      component: ({ onClose }) => (
        <ExtensionPromoModal
          onSubmit={() => {
            analytics.trackEvent('extension - promo modal click');
            window.open(
              extension.getExtensionUrl(),
              '_blank',
              'noopener,noreferrer',
            );
            onClose();
          }}
        />
      ),
    });

    updateUserExtraFields({
      notifications: {
        ...profile.extra_fields.notifications,
        extension_modal: 1,
      },
    });
  };

  const checkAndShowModal = (): void => {
    const isExtensionAvailable = extension.checkIsExtensionAvailable();
    const isExtensionInstalled = Boolean(
      document.getElementById('justdone-content-app'),
    );

    const isExtensionTest = growthBook.isTestGroup(
      GB_FEATURE_KEYS.EXTENSION_PROMO_NEW_PLACES,
    );

    // extension condition: 2nd session
    if (
      isExtensionAvailable &&
      isExtensionTest &&
      !isExtensionInstalled &&
      projectConfig.project === PROJECT.JUSTDONE &&
      currentSessionCount >= 2 &&
      !profile.extra_fields.notifications.extension_modal
    ) {
      handleShowExtensionModal();
      return;
    }

    // pwa: don’t show more than once per day
    if (
      profile.extra_fields.pwa_modal.last_showed_at &&
      Math.abs(
        DateService.differenceInDays(
          new Date(profile.extra_fields.pwa_modal.last_showed_at),
          new Date(),
        ),
      ) < 1
    ) {
      return;
    }

    const session_to_trigger_count = localStorage.getItem(
      SESSION_TO_TRIGGER_COUNT,
    );

    // pwa condition 1: first generation
    if (
      !profile.extra_fields.pwa_modal.is_showed_first_generation &&
      session_to_trigger_count &&
      currentSessionCount > Number(session_to_trigger_count) &&
      profile.is_onboarded
    ) {
      handleShowPWAModal(
        {
          is_showed_first_generation: true,
        },
        'next_visit_after_1st_gen',
      );
      return;
    }

    // pwa condition 2: 5th session
    if (
      !profile.extra_fields.pwa_modal.is_showed_fifth_session &&
      currentSessionCount >= 5 &&
      profile.is_onboarded
    ) {
      handleShowPWAModal(
        {
          is_showed_fifth_session: true,
        },
        '5th_visit',
      );
      return;
    }
  };

  useEffect(() => {
    checkAndShowModal();
  }, [profile]);

  return <>{children}</>;
};

export default PromoModalOrchestrator;
